/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Grounded in docs/agent-context/subsystems/schedule-calendar.md
 * Marketing Constraints.
 *
 * Calendar and Schedule are SEPARATE marketing targets:
 *   - Calendar (THIS page) = viewing events, iCal subscription,
 *     entity-driven auto-create. Reactive / observer UI.
 *   - Schedule (future page) = dispatch, My Day, project timelines,
 *     service visit scheduling. Active / operator UI.
 *
 * Do NOT market "Sync your Google Calendar" as bidirectional. The
 * iCal feed is ONE-WAY read-only — users subscribe from Google /
 * Outlook / Apple to the feed URL. Writes never flow back.
 *
 * Feed is bounded: -3 months to +12 months, 1000 events max,
 * 15-minute cache. Don't promise "full history" or "unlimited events".
 *
 * Backfill is one-time and rate-limited (2 per 5 minutes). Don't
 * market it as an ongoing bulk re-sync feature.
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  Calendar as CalendarIcon,
  Bell,
  Rss,
  Link2,
  Eye,
  Briefcase,
  Calculator,
  DollarSign,
  FileText,
  Clock3,
  Building2,
  AlertTriangle,
  Share2,
  Users,
  Layers,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "Calendar",
  title: "Never miss another",
  titleHighlight: "deadline or follow-up.",
  description:
    "Estimate due dates, proposal expirations, invoice due dates, service visits, project milestones, and client follow-ups are added automatically so your team sees what matters without manual calendar work.",

  featureGrid: {
    title: "A calendar that writes itself",
    items: [
      {
        icon: Link2,
        title: "Auto-Create From Six Entity Types",
        description:
          "Create an estimate, project, invoice, service, proposal, or client follow-up and a calendar event is generated automatically. No duplicate data entry, no forgotten deadlines.",
      },
      {
        icon: Rss,
        title: "iCal Feed Subscription",
        description:
          "Subscribe your personal Google Calendar, Outlook, or Apple Calendar to your CC feed with a single URL. Events show up alongside your existing commitments, always read-only and always current.",
      },
      {
        icon: CalendarIcon,
        title: "Month / Week / Day / Agenda Views",
        description:
          "Four ways to look at the same events. Month for overview, week for planning, day for focus, agenda for a flat list. Your view preference sticks between sessions.",
      },
      {
        icon: Bell,
        title: "Automated Reminders",
        description:
          "Get reminders for upcoming due dates, expirations, and overdue invoices before they become expensive misses.",
      },
      {
        icon: Layers,
        title: "Entity-Linked Events",
        description:
          "Events stay tied to the business context that created them so teams can move from schedule view back to the related work quickly.",
      },
      {
        icon: Building2,
        title: "Division Scoping",
        description:
          "Events carry a division tag. Filter your view to just residential, just commercial, just design-build, or roll up the whole company.",
      },
      {
        icon: AlertTriangle,
        title: "Priority & Status",
        description:
          "Priority and status fields on every event. Overdue items are called out automatically. Recurring event patterns supported.",
      },
      {
        icon: Eye,
        title: "Filter & Color Code",
        description:
          "Filter by event type, status, or linked entity. Color coding by type so you can scan a month at a glance and know what's sales, what's project milestones, and what's invoice deadlines.",
      },
      {
        icon: Share2,
        title: "One-Click Subscribe",
        description:
          "Copy your subscribe link once and view CC events in the calendar app your team already uses.",
      },
    ],
  },

  workflow: {
    title: "How the calendar fills itself up",
    description:
      "Your team stays on top of deadlines because key events are created from real work, not manual calendar entry.",
    steps: [
      {
        label: "Generate",
        title: "Create work and let events appear automatically",
        description:
          "As your team creates estimates, invoices, projects, and service work, related deadlines and milestones are added automatically.",
        details: [
          "Deadline creation from active workflows",
          "Milestone visibility without duplicate entry",
          "Better consistency across teams",
          "Reduced missed commitments",
        ],
      },
      {
        label: "Review",
        title: "See priorities by day, week, or month",
        description:
          "Use the view that fits your planning rhythm and filter by type or division so the right priorities stand out quickly.",
        details: [
          "Flexible planning views",
          "Type and division filtering support",
          "Event-to-record context links",
          "Cleaner weekly planning sessions",
          "Improved team coordination",
        ],
      },
      {
        label: "Share",
        title: "Subscribe from your external calendar apps",
        description:
          "Bring CC events into your existing Google, Outlook, or Apple calendar view so important deadlines show up where your team already plans.",
        details: [
          "Simple subscribe-link workflow",
          "Compatibility with major calendar apps",
          "Read-only external calendar behavior",
          "Current and upcoming schedule visibility",
          "No manual exports required",
        ],
      },
      {
        label: "Get Reminded",
        title: "Never miss a deadline",
        description:
          "Receive reminders for upcoming due dates and overdue risks so follow-up happens before problems escalate.",
        details: [
          "Estimate and proposal follow-up support",
          "Invoice due and overdue reminder visibility",
          "Upcoming deadline notifications",
          "Preference-aware reminder behavior",
          "Lower risk of missed critical dates",
        ],
      },
    ],
  },

  capabilities: [
    {
      icon: Link2,
      title: "Six Auto-Sync Wrappers — You Never Touch the Calendar Manually",
      description:
        "CC wraps every major entity write with a calendar sync helper. Create or update an estimate, project, invoice, service, proposal, or client follow-up and the corresponding calendar event is created, updated, or archived in the same transaction. You don't manage two systems — you run your business and the calendar keeps itself current.",
      bullets: [
        "Estimate due dates auto-sync to calendar",
        "Project start / end dates auto-sync",
        "Invoice due dates auto-sync",
        "Service scheduled dates auto-sync",
        "Proposal expiration auto-sync",
        "Client follow-up dates auto-sync",
        "Writes happen at entity write time — no polling, no stale data",
      ],
    },
    {
      icon: Rss,
      title: "iCal Feed Subscription — Works With Every Major Calendar",
      description:
        "Most contractor tools give you a calendar inside their app and call it a day. CC generates a secure iCal feed URL per user so you can subscribe from Google Calendar, Outlook, or Apple Calendar and see your CC deadlines next to your personal commitments. The feed is read-only and stays current — no exports, no CSVs, no manual refresh.",
      bullets: [
        "Secure per-user subscription links",
        "Subscribe URL copyable from the Calendar page",
        "Works with Google Calendar, Outlook, Apple Calendar",
        "Bounded range: -3 months to +12 months",
        "Up to 1000 events per feed",
        "Fast periodic refresh behavior",
        "Read-only — edits stay in CC where they belong",
      ],
    },
    {
      icon: Layers,
      title: "Entity Context You Can Trust",
      description:
        "Calendar events carry structured context from the workflows that created them, helping teams understand what each deadline is tied to and where to continue work.",
      bullets: [
        "Context-aware links to related records",
        "Support for client, estimate, proposal, supplier, and division context",
        "Project and service schedule context where available",
        "Click-through from event to source entity",
        "Entity-scoped filters on detail pages",
        "Soft-delete support for archiving without data loss",
      ],
    },
    {
      icon: Bell,
      title: "Deadline Reminders Driven By Real Data",
      description:
        "CC runs background jobs that watch the underlying data — not a user-defined reminder list — and fires notifications when deadlines approach. Estimate follow-ups, proposal expirations, invoice upcoming and overdue reminders, calendar event reminders, and custom deadline alerts all flow through the same notification center. Preferences are respected so you don't get paged for events you don't care about.",
      bullets: [
        "Cron-driven: invoice reminders, calendar reminders, deadline reminders",
        "Proactive watching of underlying entity dates",
        "Notifications delivered to the in-app center",
        "Per-user notification preferences respected",
        "Realtime subscription so the bell updates instantly",
      ],
    },
  ],

  connections: {
    title: "How the calendar connects to the rest of CC",
    items: [
      {
        icon: Calculator,
        flow: "Estimate → Calendar",
        description:
          "Every estimate with a due date becomes a calendar event. Change the due date on the estimate and the event updates. Close the estimate and the event archives.",
      },
      {
        icon: FileText,
        flow: "Proposal → Expiration Reminder",
        description:
          "Sent proposals carry an expiration date. The calendar event and reminder chain wake up as the expiration approaches so you know to follow up before it lapses.",
      },
      {
        icon: DollarSign,
        flow: "Invoice → Payment Due",
        description:
          "Every invoice's due date lands on the calendar. Upcoming and overdue reminders fire from scheduled cron jobs — you never have to remember to chase a payment manually.",
      },
      {
        icon: Briefcase,
        flow: "Project & Service → Milestones",
        description:
          "Project milestones and scheduled service visits appear in calendar views so operations teams can plan around active execution timelines.",
      },
      {
        icon: Users,
        flow: "Client → Follow-Up",
        description:
          "Schedule a follow-up on a client and the calendar event is created automatically. Your sales reminder list runs on structured data, not a notebook.",
      },
    ],
  },

  relatedFeatures: [
    {
      label: "Projects & Services",
      href: "/features/project-management",
      icon: Briefcase,
    },
    {
      label: "Estimates & Takeoffs",
      href: "/features/estimates",
      icon: Calculator,
    },
    {
      label: "Proposals & Client Portal",
      href: "/features/proposals-portal",
      icon: FileText,
    },
    {
      label: "Budgets & Invoices",
      href: "/features/budgets-invoices",
      icon: DollarSign,
    },
    {
      label: "CRM",
      href: "/features/crm",
      icon: Users,
    },
    {
      label: "Team & Permissions",
      href: "/features/collaboration",
      icon: Clock3,
    },
  ],
};

export function CalendarPageClient() {
  return <FeaturePageTemplate config={config} />;
}
